import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { copyBackupCodes, downloadBackupCodes, printBackupCodes } from '../../utils/backupCodes';

interface TwoFactorEnrollProps {
    challengeId: string;
    qrDataUri: string;
    secret: string;
    onSuccess: (backupCodes: string[]) => void;
    onCancel: () => void;
}

const TwoFactorEnroll: React.FC<TwoFactorEnrollProps> = ({ challengeId, qrDataUri, secret, onSuccess, onCancel }) => {
    const { completeTwoFactorLogin } = useAuth();
    const [code, setCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(true);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = async (codes: string[]) => {
        await copyBackupCodes(codes);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await completeTwoFactorLogin(challengeId, code, rememberDevice);
            if (result.success && result.backupCodes) {
                setBackupCodes(result.backupCodes);
            } else if (result.success) {
                // Success but no backup codes returned — malformed response
                setError('Enrollment succeeded but no backup codes were returned. Please contact support.');
            } else if (!result.success) {
                setError(result.error || 'Invalid or expired code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (backupCodes) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={600} mb={2}>
                    Save Your Backup Codes
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Each code can be used once if you lose access to your authenticator app. Store them somewhere safe — they won't be shown again.
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2, fontFamily: 'monospace' }}>
                    {backupCodes.map((c) => <div key={c}>{c}</div>)}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button size="small" onClick={() => handleCopy(backupCodes)}>{copied ? 'Copied!' : 'Copy'}</Button>
                    <Button size="small" onClick={() => downloadBackupCodes(backupCodes)}>Download</Button>
                    <Button size="small" onClick={() => printBackupCodes(backupCodes)}>Print</Button>
                </Box>
                <FormControlLabel
                    control={<Checkbox checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />}
                    label="I've saved these backup codes"
                    sx={{ mb: 2 }}
                />
                <Button
                    variant="contained"
                    fullWidth
                    disabled={!savedConfirmed}
                    onClick={() => onSuccess(backupCodes)}
                >
                    Continue
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} mb={2}>
                Set Up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Your institution requires two-factor authentication. Scan this QR code with an authenticator app
                (Google Authenticator, Authy, etc.), then enter the 6-digit code it shows.
            </Typography>

            <Box textAlign="center" sx={{ mb: 2 }}>
                <img src={qrDataUri} alt="Two-factor QR code" style={{ width: 200, height: 200 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Can't scan? Enter this code manually: <strong>{secret}</strong>
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleConfirm}>
                <TextField
                    label="Authentication Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    autoFocus
                />
                <FormControlLabel
                    control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
                    label="Remember this device for 30 days"
                    sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mb: 2 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
                </Button>
            </form>

            <Box textAlign="center">
                <Button variant="text" onClick={onCancel}>Cancel</Button>
            </Box>
        </Box>
    );
};

export default TwoFactorEnroll;
